import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNamespacedTranslation } from '../../../../hooks/useNamespacedTranslation';
import { useFlairPersistentFilter } from '../../../../hooks/useFlairPersistentFilter';
import { useQueryParameter } from '../../../../hooks/useQueryParameter';
import {
  CorporateDocument,
  DocumentPreview,
  emptyCorporateDocumentFilter,
  File,
} from './types';
import { findDeepLinkTarget } from './helpers';
import { routes as mainRoutes } from '../../routes';
import { Card } from 'react-bootstrap';
import { CorporateDocumentsTable } from './Tables/CorporateDocumentsTable';
import { useCorporateDocumentsQuery } from '../../__generated__/graphql';
import { SkeletonTable } from '../../components/Skeleton/Table';
import ServerError from '../../../../components/ServerError';
import {
  mapCorporateDocumentPreview,
  mapCorporateDocumentPreviewWithoutFile,
  mapCorporateDocuments,
  mapCorporateFolders,
} from './mappings';
import { isPreviewableType } from './DocumentInfo/DocumentPreview';
import { DocumentPreviewModal } from './DocumentInfo/DocumentPreviewModal';
import { DocumentInfoSidebar } from './DocumentInfo/DocumentInfoSidebar';
import {
  applyCorporateDocumentsFilter,
  hasCorporateDocumentFilter,
  isCorporateDocumentNameSearchActive,
} from './Fliters/filters';
import GlobalTableFilter from '../../components/GlobalTableFilter';
import FlairIcon from '../../../../atomic/atoms/FlairIcon';

const i18Path = 'documents2.corporate';
const FOLDER_QUERY_PARAM = 'folder';
// Deep link used by AI assistant citations: ?document=<id> opens the preview.
const DOCUMENT_QUERY_PARAM = 'document';
// Matches the Apex MAX_FOLDER_DEPTH cap; protects breadcrumb walks from
// pathological cycles in folder data.
const MAX_FOLDER_DEPTH = 20;

type FolderBreadcrumb = { id: string | null; name: string };

export const CorporateDocuments: React.FC = () => {
  const { data, loading, error } = useCorporateDocumentsQuery();
  const [row, setDocumentRow] = useState<DocumentPreview | null>(null);
  const [previewVisible, setDocumentPreview] = useState<boolean>(false);
  const [sidebarVisible, setDocumentSidebar] = useState<boolean>(false);

  const [currentFolderId, setCurrentFolderId] =
    useQueryParameter(FOLDER_QUERY_PARAM);
  const [deepLinkDocumentId] = useQueryParameter(DOCUMENT_QUERY_PARAM);

  const t = useNamespacedTranslation(i18Path);
  const [filter, setFilter] = useFlairPersistentFilter({
    defaultFilter: emptyCorporateDocumentFilter,
    storageKey: 'corporate_document_filter',
    createRouteUrl: (queryParams) =>
      mainRoutes.peopleDocuments.route.withQueryParams(queryParams).create({}),
  });

  const allDocuments = useMemo(
    () => (data ? mapCorporateDocuments(data) : []),
    [data],
  );
  const allFolders = useMemo(
    () => (data ? mapCorporateFolders(data) : []),
    [data],
  );

  const folderById = useMemo(
    () => new Map(allFolders.map((f) => [f.id, f])),
    [allFolders],
  );

  const breadcrumbs = useMemo<FolderBreadcrumb[]>(() => {
    const result: FolderBreadcrumb[] = [{ id: null, name: t('table.home') }];
    if (currentFolderId === null) return result;

    const trail: FolderBreadcrumb[] = [];
    let cursor: string | null = currentFolderId;
    for (let i = 0; cursor && i < MAX_FOLDER_DEPTH; i++) {
      const folder = folderById.get(cursor);
      if (!folder) break;
      trail.unshift({ id: folder.id, name: folder.name });
      cursor = folder.parentFolderId;
    }
    return [...result, ...trail];
  }, [folderById, currentFolderId, t]);

  // If the URL points to a folder that doesn't exist (deleted, stale bookmark),
  // silently fall back to the root so the user isn't stranded with hidden breadcrumbs.
  useEffect(() => {
    if (!data) return;
    if (currentFolderId === null) return;
    if (!folderById.has(currentFolderId)) {
      setCurrentFolderId(null);
    }
  }, [data, currentFolderId, folderById, setCurrentFolderId]);

  const openDocument = useCallback(
    (document: CorporateDocument, file?: File) => {
      if (file) {
        setDocumentRow(mapCorporateDocumentPreview(document, file));
        if (isPreviewableType(file.type)) {
          setDocumentPreview(true);
        } else {
          setDocumentSidebar(true);
        }
      } else {
        setDocumentRow(mapCorporateDocumentPreviewWithoutFile(document));
        setDocumentPreview(true);
      }
    },
    [],
  );

  // Deep link used by AI assistant citations (?document=<id>): once documents
  // are loaded, jump to the document's folder and open its preview. The ref
  // guards against re-opening after the user closes the modal (the param stays
  // in the URL because two useQueryParameter setters in one tick clobber each
  // other's snapshot).
  const handledDeepLink = useRef(false);
  useEffect(() => {
    if (!data || handledDeepLink.current) {
      return;
    }
    const target = findDeepLinkTarget(allDocuments, deepLinkDocumentId);
    if (!target) {
      return;
    }
    handledDeepLink.current = true;
    setCurrentFolderId(target.document.folderId);
    openDocument(target.document, target.file);
  }, [
    data,
    allDocuments,
    deepLinkDocumentId,
    setCurrentFolderId,
    openDocument,
  ]);

  const isSearching = isCorporateDocumentNameSearchActive(filter.documentName);

  const filteredDocuments = useMemo(
    () => applyCorporateDocumentsFilter(filter, allDocuments),
    [filter, allDocuments],
  );
  const documentsInScope = useMemo(
    () =>
      isSearching
        ? filteredDocuments
        : filteredDocuments.filter((d) => d.folderId === currentFolderId),
    [isSearching, filteredDocuments, currentFolderId],
  );
  const subFolders = useMemo(
    () =>
      isSearching
        ? []
        : allFolders.filter((f) => f.parentFolderId === currentFolderId),
    [isSearching, allFolders, currentFolderId],
  );

  if (error) {
    return <ServerError />;
  }

  const onClick = (documentId: string, file?: File) => {
    const document = allDocuments.find((d) => d.id === documentId);
    if (!document) return;
    openDocument(document, file);
  };

  const handleDownload = (url?: string) => {
    if (url) {
      window.open(url, '_blank');
    }
  };

  const handlePreviewClose = () => setDocumentPreview(false);
  const handleSideBarClose = () => setDocumentSidebar(false);

  const onSearch = (val: string) => {
    const trimmed = val.trim();
    setFilter({
      ...filter,
      documentName: trimmed.length ? trimmed : null,
    });
  };

  const renderContent = () => {
    if (loading || !data) {
      return <Loading />;
    }
    return (
      <CorporateDocumentsTable
        hasCorporateDocumentFilter={hasCorporateDocumentFilter(filter)}
        insideFolder={currentFolderId !== null}
        documents={documentsInScope}
        folders={subFolders}
        onClick={onClick}
        onFolderClick={(folderId) => setCurrentFolderId(folderId)}
      />
    );
  };

  return (
    <>
      <Card>
        <Card.Header className="h-auto">
          <GlobalTableFilter
            placeholder={t('search')}
            onFilterChange={onSearch}
          />
        </Card.Header>
        {breadcrumbs.length > 1 && (
          <Card.Header>
            <div className="d-flex gap-3 align-items-center small">
              <Breadcrumbs
                breadcrumbs={breadcrumbs}
                onClick={(id) => setCurrentFolderId(id)}
              />
            </div>
          </Card.Header>
        )}
        {renderContent()}
      </Card>

      <DocumentPreviewModal
        visible={previewVisible}
        isPersonal={false}
        onDownload={handleDownload}
        document={row}
        onClose={handlePreviewClose}></DocumentPreviewModal>

      <DocumentInfoSidebar
        visible={sidebarVisible}
        isPersonal={false}
        onDownload={handleDownload}
        document={row}
        onClose={handleSideBarClose}></DocumentInfoSidebar>
    </>
  );
};

const Loading: React.FC = () => {
  return <SkeletonTable columns={3} rows={5} />;
};

type BreadcrumbsProps = {
  breadcrumbs: FolderBreadcrumb[];
  onClick: (id: string | null) => void;
};

const Breadcrumbs: React.FC<BreadcrumbsProps> = ({ breadcrumbs, onClick }) => {
  return (
    <div className="d-flex flex-wrap align-items-center">
      {breadcrumbs.map((crumb, idx) => {
        const isLast = idx === breadcrumbs.length - 1;
        return (
          <React.Fragment key={crumb.id ?? 'root'}>
            {isLast ? (
              <span className="h7-medium">{crumb.name}</span>
            ) : (
              <span
                className="cu-pointer text-primary"
                onClick={() => onClick(crumb.id)}>
                {crumb.name}
              </span>
            )}
            {!isLast && (
              <FlairIcon
                icon="chevron-forward-outline"
                className="mx-2"
                size="sm"
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};
